import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";
import { isAfter } from "date-fns/esm";

import useLayoutStore from "components/Layout/stores/useLayoutStore";
import MainNavigationNavigationItem from "components/MainNavigation/components/elements/NavigationItem";
import { SIDEBAR_CUSTOMIZATION_PATH } from "components/MainNavigation/components/layout/SettingsButton";
import {
  MAIN_NAVIGATION_PRIMARY_MENU_ITEMS,
  MainNavigationPrimaryMenuItemKeys,
} from "components/MainNavigation/config/menuItemsPrimary";
import useMainNavigationAnnouncement from "components/MainNavigation/hooks/useAnnouncement";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import { MainNavigationAnnouncementProps } from "components/MainNavigation/types";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import TooltipModal from "ds/components/TooltipModal";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import useAssistantPanelAvailable from "hooks/useAssistantPanelAvailable";
import useTypedFlags from "hooks/useTypedFlags";

import { NEW_HOME_ANNOUNCEMENT_ID } from "../constants";

//TODO: Just remove the announcement code after this date
export const NEW_HOME_ANNOUNCEMENT_EXPIRES_AT = new Date("2026-11-01");
export const isNewHomeAnnouncementExpired = (now: number) =>
  isAfter(now, NEW_HOME_ANNOUNCEMENT_EXPIRES_AT);

const isExpired = isNewHomeAnnouncementExpired(Date.now());

const MainNavigationNewHomeAnnouncement = ({ item }: MainNavigationAnnouncementProps) => {
  const { disableInfraAssistantDefaultView } = useTypedFlags();
  const isAssistantPanelAvailable = useAssistantPanelAvailable();
  const isFullScreenModalOpen = useLayoutStore((state) => state.isFullScreenModalOpen);
  const { defaultViewItemId, setDefaultViewItemId } = useMainNavigationStore(
    useShallow((state) => ({
      defaultViewItemId: state.defaultViewItemId,
      setDefaultViewItemId: state.setDefaultViewItemId,
    }))
  );
  const { isDismissed, dismiss } = useMainNavigationAnnouncement(NEW_HOME_ANNOUNCEMENT_ID);

  const handleSetAsDefaultView = useCallback(() => {
    setDefaultViewItemId(MainNavigationPrimaryMenuItemKeys.AiChat);
    dismiss();
  }, [setDefaultViewItemId, dismiss]);

  const isAlreadyDefaultView = defaultViewItemId === MainNavigationPrimaryMenuItemKeys.AiChat;

  const isVisible =
    isAssistantPanelAvailable &&
    !isFullScreenModalOpen &&
    !disableInfraAssistantDefaultView &&
    !isDismissed &&
    !isAlreadyDefaultView &&
    !isExpired;
  const hasCustomDefaultView = !!defaultViewItemId;

  const customDefaultViewTitle = Object.values(MAIN_NAVIGATION_PRIMARY_MENU_ITEMS).find(
    (menuItem) => menuItem.id === defaultViewItemId
  )?.title;

  return (
    <TooltipModal
      placement="right"
      active={false}
      forceOpen={isVisible}
      closeOnOutsideClick={false}
      disableImplicitClose
      isNonModal
      closeCallback={dismiss}
      on={({ ref }) => <MainNavigationNavigationItem item={item} tooltipModalRef={ref} />}
    >
      <TooltipModalTitle>Meet your new home - Infra Assistant</TooltipModalTitle>

      {!hasCustomDefaultView && (
        <TooltipModalBody align="start">
          Infra Assistant is now your home page, so you can start asking about your infrastructure
          right away. You can change this in the sidebar customization settings.
          <LinkButton
            variant="secondary"
            size="small"
            to={SIDEBAR_CUSTOMIZATION_PATH}
            onClick={dismiss}
          >
            Customize sidebar
          </LinkButton>
        </TooltipModalBody>
      )}

      {hasCustomDefaultView && (
        <TooltipModalBody align="start">
          Set Infra Assistant as your default view to jump straight into asking about your
          infrastructure.
          <Box gap="medium" align="center">
            <Button variant="primary" size="small" onPress={handleSetAsDefaultView}>
              Set as default view
            </Button>
            <Button variant="ghost" size="small" onPress={dismiss}>
              {customDefaultViewTitle ? `Keep ${customDefaultViewTitle}` : "Keep my default view"}
            </Button>
          </Box>
        </TooltipModalBody>
      )}
    </TooltipModal>
  );
};

export default MainNavigationNewHomeAnnouncement;
